// SingleVideoShowcase.jsx
import React, { useState, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
  IconButton,
  LinearProgress,
  Fade,
} from "@mui/material";
import InsightsOutlined from "@mui/icons-material/InsightsOutlined";
import StarRounded from "@mui/icons-material/StarRounded";
import ArrowRightAltRounded from "@mui/icons-material/ArrowRightAltRounded";
import { 
  ArrowRightRounded, 
  PlayArrow, 
  Pause, 
  VolumeUp, 
  VolumeOff,
  Download 
} from "@mui/icons-material";
import { motion } from "framer-motion";

const gradientBg =
  "linear-gradient(90deg, rgba(10,25,47,1) 0%, rgba(23,42,69,1) 11%, rgba(23,42,69,1) 45%, rgba(2,12,27,1) 100%)";

/**
 * Props:
 *  item: {
 *    title: string,
 *    subtitle?: string,
 *    features?: string[],
 *    link: string // video URL (MP4)
 *    thumbnail?: string // optional thumbnail image URL
 *  }
 */
export default function VideoContainer({ item }) {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true); // Start muted for autoplay
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const videoRef = useRef(null);

  if (!item) return null;

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleMuteToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVideoLoaded = () => {
    setIsLoading(false);
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const current = videoRef.current.currentTime;
      const duration = videoRef.current.duration;
      if (duration > 0) {
        setProgress((current / duration) * 100);
      }
    }
  };

  const handleVideoEnded = () => {
    // Loop back to start
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleDownload = () => {
    if (item.link) {
      const link = document.createElement('a');
      link.href = item.link;
      link.download = item.title || 'video.mp4';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 4, md: 8 },
        mx: "auto",
        maxWidth: 1200,
      }}
    >
      {/* Video Container */}
      <Box
        sx={(theme) => ({
          position: "relative",
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: theme.shadows[6],
          backgroundColor: '#000',
        })}
      >
        {/* Video Player */}
        <Box sx={{ position: 'relative', aspectRatio: '16/9' }}>
          <video
            ref={videoRef}
            src={item.link}
            poster={item.thumbnail}
            autoPlay
            muted={isMuted}
            loop
            playsInline
            onLoadedData={handleVideoLoaded}
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleVideoEnded}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />

          {/* Loading Overlay */}
          {isLoading && (
            <Fade in={isLoading}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.7)',
                }}
              >
                <Typography variant="h6" color="white">
                  Loading video...
                </Typography>
              </Box>
            </Fade>
          )}

          {/* Play/Pause Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: 'none',
            }}
          />

          {/* Video Controls */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
            }}
          >
            {/* Progress Bar */}
            <Box sx={{ flex: 1, position: 'relative' }}>
              <LinearProgress 
                variant="determinate" 
                value={progress}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 2,
                    backgroundColor: '#1976d2',
                  }
                }}
              />
            </Box>

            {/* Control Buttons */}
            <Box sx={{ display: 'flex', gap: 1, pointerEvents: 'auto' }}>
              <IconButton 
                onClick={handlePlayPause}
                size="small"
                sx={{ 
                  backgroundColor: 'rgba(25, 118, 210, 0.9)',
                  '&:hover': { backgroundColor: 'rgba(25, 118, 210, 1)' },
                  color: 'white'
                }}
              >
                {isPlaying ? <Pause /> : <PlayArrow />}
              </IconButton>
              
              <IconButton 
                onClick={handleMuteToggle}
                size="small"
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' },
                  color: 'white'
                }}
              >
                {isMuted ? <VolumeOff /> : <VolumeUp />}
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Floating corner badge */}
        <Chip
          icon={<InsightsOutlined />}
          label="Video Overview"
          color="secondary"
          variant="filled"
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "rgba(255,255,255,0.6)",
            backdropFilter: "blur(6px)",
            "& .MuiChip-label": { fontWeight: 700 },
            zIndex: 1,
          }}
        />
      </Box>

      {/* Information panel */}
      <Card
        elevation={4}
        sx={{
          mt: 3,
          borderRadius: 3,
          overflow: "hidden",
          border: (t) => `1px solid ${t.palette.primary.main}`,
        }}
      >
        {/* Gradient headline bar */}
        <Box
          sx={{
            px: 3,
            py: 2,
            background: gradientBg,
            color: "white",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack spacing={0.5}>
              <Typography variant="h5" fontWeight={800} lineHeight={1.2}>
                {item.title}
              </Typography>
              {item.subtitle && (
                <Typography variant="body1" sx={{ opacity: 0.85 }}>
                  {item.subtitle}
                </Typography>
              )}
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                icon={<StarRounded />}
                label="Featured"
                color="warning"
                variant="filled"
                size="small"
                sx={{ fontWeight: 700 }}
              />
              <Chip
                icon={<ArrowRightAltRounded />}
                label="Demo"
                color="secondary"
                variant="outlined"
                size="small"
              />
              <Chip
                icon={isPlaying ? <Pause /> : <PlayArrow />}
                label={isPlaying ? "Playing" : "Paused"}
                color="primary"
                variant="outlined"
                size="small"
                onClick={handlePlayPause}
                sx={{ cursor: 'pointer' }}
              />
            </Stack>
          </Stack>
        </Box>

        <CardContent 
          initial={{ opacity: 0}} 
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }} 
          component={motion.div} 
          sx={{ px: { xs: 2, md: 3 }, py: 3 }}
        >
          {/* Feature bullets */}
          <Stack spacing={1.5}>
            <Typography variant="overline" color="text.secondary">
              Key Features
            </Typography>
            <Divider />
            <Stack
              spacing={1}
              sx={{
                "& .feature": {
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontWeight: 600,
                },
              }}
            >
              {(item.features || []).map((text, idx) => (
                <Typography key={idx} className="feature" variant="body1">
                  <ArrowRightRounded
                    fontSize="small"
                    color="secondary"
                    sx={{ mt: "2px" }}
                  />
                  {text}
                </Typography>
              ))}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}